'use client';

import { type ReactNode } from 'react';

import { useBioUserAccess } from '@ps/features/bio/contexts/user-access/useBioUserAccess';

import { useOnCheckoutSuccess } from '../../../../checkout/notification/useOnCheckoutSuccess';
import {
  bumpCoursePurchaseGeneration,
  markCoursePurchased,
} from '../../../../courses/shared/hooks/coursePurchaseCache';
import { useCourseOwnershipRegistry } from '../../../../courses/shared/hooks/useCourseOwnership';
import { CoursePurchaseContext } from '../../../shared/contexts/course-purchase/context';

/**
 * Resolves the viewer's course-purchase ownership for the WHOLE bio, keyed by
 * each course's derived `listingId`. Surfaces (the "Unlock this course" banner
 * + every clip lock-overlay, on the course page AND in every off-course My
 * Life / home rail) register the listingId they need; the registry resolves
 * each once — deduped process-wide via the shared cache — so a purchaser sees
 * neither the unlock CTA nor a lock badge anywhere, mirroring PopshopRN's
 * global course-order cache.
 *
 * Mount ONCE, INSIDE `BioUserAccessProvider` (it takes the viewer id from
 * there) — the profile layout does this so it spans all tabs. Additional
 * instances (course detail, clip player) are cheap: the shared cache collapses
 * their overlapping requests into one.
 *
 * SAME-SESSION unlock (RN `coursePurchased$` parity): when a `course` checkout
 * completes, the just-bought course must flip to owned with no reload. The
 * backend order isn't searchable the instant checkout succeeds, so a plain
 * re-query races the index and can come back `false` — leaving the unlock CTA +
 * clip lock badges stale until a manual refresh (the QA report). So:
 *
 *  - A COURSE-SCOPED mount (given `courseListingId` + `courseSlug` — the course
 *    detail, chapter, and clip-player surfaces) OPTIMISTICALLY marks its own
 *    course owned when a `course` success matches its slug — RN's
 *    `coursePurchased$` flip. This clears the lock immediately, no request.
 *  - A bio-WIDE mount (no course scope) just bumps the generation, so every
 *    registry re-resolves (picking up the optimistic flip a scoped mount made).
 *
 * The success event carries `course_slug`/`c`, not the derived listingId — hence
 * the slug match here, where the surface knows both its slug and its listingId.
 */
export function CoursePurchaseProvider({
  courseListingId,
  courseSlug,
  children,
}: {
  /** The derived listing of the course this mount is scoped to, when known —
   *  enables the optimistic same-session unlock. Omit on the bio-wide mount. */
  courseListingId?: string;
  /** The slug of the course this mount is scoped to — matched against the
   *  checkout success event (which carries `course_slug`, not the listingId). */
  courseSlug?: string;
  children: ReactNode;
}) {
  const { currentUserId } = useBioUserAccess();

  useOnCheckoutSuccess(event => {
    if (event.type !== 'course') return;
    // This mount's OWN course was just bought → optimistically mark it owned so
    // the flip survives the backend order-index lag (and bumps the generation
    // itself). Otherwise (bio-wide, or a different course) just re-resolve.
    if (
      currentUserId != null &&
      courseListingId != null &&
      courseSlug != null &&
      event.params.course_slug === courseSlug
    ) {
      markCoursePurchased(currentUserId, courseListingId);
      return;
    }
    bumpCoursePurchaseGeneration();
  });

  const registry = useCourseOwnershipRegistry(currentUserId);

  return (
    <CoursePurchaseContext.Provider value={registry}>
      {children}
    </CoursePurchaseContext.Provider>
  );
}
